import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Loader2, MessageCircle, ChevronUp } from "lucide-react";

import HeroAston from "./HeroCarousel";
import RimacTransition from "./RimacTransition";
import TeslaLineup from "./TeslaLineup";
import SearchPanel from "./SearchPanel";
import VehicleGridPremium from "./VehicleGridPremium";
import FinancingSimulator from "./FinancingSimulator";
import BenefitsSection from "./BenefitsSection";
import HowItWorks from "./HowItWorks";
import StatsSection from "./StatsSection";
import PremiumFooter from "./PremiumFooter";
import AssistantWidget from "./AssistantWidget";
import { getCached, setCached } from "./catalogCache";

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_BUSINESS = {
  id: "automotriz",
  slug: "automotriz",
  name: "HEBEH Automotriz",
  itemLabel: "vehículo",
  itemLabelPlural: "vehículos",
  accentColor: "#F09A21",
  whatsapp: "5539835137",
  phone: "55 1318 1745",
};

// Group vehicles for automotriz sale: one card per brand+model+year, showing cheapest.
function groupByModel(list) {
  const toNum = (p) => {
    if (!p) return 0;
    const n = parseFloat(String(p).replace(/[,$]/g, ""));
    return isNaN(n) ? 0 : n;
  };
  const groups = new Map();
  for (const v of list) {
    const key = `${(v.brand || "").toLowerCase().trim()}|${(v.model_name || "").toLowerCase().trim()}|${(v.year || "").toString().trim()}`;
    const existing = groups.get(key);
    if (!existing) groups.set(key, { base: v, count: 1 });
    else {
      existing.count += 1;
      if (toNum(v.price) > 0 && (toNum(existing.base.price) === 0 || toNum(v.price) < toNum(existing.base.price))) {
        existing.base = v;
      }
    }
  }
  return Array.from(groups.values()).map(({ base, count }) => ({
    ...base, _grouped: true, _versionsCount: count,
  }));
}

function FloatingActions({ business }) {
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const wa = business?.whatsapp || "5539835137";
  return (
    <div className="fixed right-5 bottom-20 z-50 flex flex-col gap-3 items-end">
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="w-10 h-10 rounded-full bg-white/95 text-[#0B1623] shadow-[0_8px_24px_rgba(11,22,35,0.15)] flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Ir arriba"
          data-testid="float-scroll-top"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
      )}
      <a
        href={`https://api.whatsapp.com/send?phone=${wa}&text=${encodeURIComponent("Hola, vi el catálogo y me interesa un auto")}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-12 h-12 rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_rgba(37,211,102,0.35)] flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="WhatsApp"
        data-testid="float-whatsapp"
      >
        <MessageCircle className="w-5 h-5" />
      </a>
    </div>
  );
}

function HeaderBar({ business }) {
  const [phase, setPhase] = useState("dark"); // "dark" (transparent over hero) | "light" (over cream sections)
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      // Threshold ~ hero + rimac (aprox 180vh). Adjust as needed.
      const heroH = Math.max(640, window.innerHeight * 1);
      const rimacH = Math.max(560, window.innerHeight * 0.8);
      const threshold = heroH + rimacH - 200;
      setPhase(y > threshold ? "light" : "dark");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const isLight = phase === "light";
  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
        isLight ? "bg-[#FFFFFF]/85 backdrop-blur-xl border-b border-[#0B1623]/8" : "bg-transparent"
      }`}
      data-testid="premium-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to={`/catalogo/${business.slug}`} className="flex items-center gap-3">
          {business.logoUrl ? (
            <img src={business.logoUrl} alt="HEBEH" className="h-10 md:h-12 w-auto" />
          ) : (
            <span className={`font-rajdhani font-bold text-xl tracking-[0.15em] uppercase transition-colors ${isLight ? "text-[#0B1623]" : "text-white"}`}>
              HEBEH
            </span>
          )}
          <span className={`hidden md:inline font-rajdhani text-xs tracking-[0.4em] uppercase transition-colors ${isLight ? "text-[#0B1623]/60" : "text-white/60"}`}>
            Automotriz
          </span>
        </Link>
        <nav className={`hidden md:flex items-center gap-8 font-rajdhani font-medium text-sm tracking-[0.2em] uppercase transition-colors ${isLight ? "text-[#0B1623]/70" : "text-white/70"}`}>
          <a href="#catalogo" className="hover:opacity-100 opacity-80 transition-opacity">Catálogo</a>
          <a href="#como-funciona" className="hover:opacity-100 opacity-80 transition-opacity">Proceso</a>
          <a
            href={`https://api.whatsapp.com/send?phone=${business.whatsapp || "5539835137"}`}
            target="_blank" rel="noopener noreferrer"
            className="hover:opacity-100 opacity-80 transition-opacity"
          >Contacto</a>
        </nav>
        <a
          href={`tel:${business.whatsapp || "5539835137"}`}
          className={`inline-flex items-center gap-2 px-5 py-2 rounded-full border font-rajdhani font-medium text-sm tracking-[0.2em] uppercase transition-colors ${
            isLight
              ? "border-[#0B1623]/25 text-[#0B1623] hover:bg-[#0B1623] hover:text-white"
              : "border-white/25 text-white hover:bg-white hover:text-[#0B1623]"
          }`}
          data-testid="header-cta-call"
        >
          Llamar
        </a>
      </div>
    </header>
  );
}

export default function CatalogPagePremium() {
  const { businessSlug } = useParams();
  const [vehicles, setVehicles] = useState([]);
  const [rentalVehicles, setRentalVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [business, setBusiness] = useState({ ...DEFAULT_BUSINESS, slug: businessSlug || "automotriz" });
  const [interestRate, setInterestRate] = useState(20);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [viewMode, setViewMode] = useState("sale");

  // === SEO landing (portada del catálogo) ===
  useEffect(() => {
    document.title = "HEBEH Automotriz | Nissan, Chevrolet, JAC y KIA — Autos nuevos CDMX";
    const set = (attr, key, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value || "");
    };
    const desc = "Agencia HEBEH Automotriz en Ciudad de México. Autos nuevos Nissan, Chevrolet, JAC y KIA con financiamiento a tasa preferencial. Cotiza por WhatsApp al instante.";
    set("name", "description", desc);
    set("name", "keywords", "autos nuevos CDMX, agencia Nissan México, agencia Chevrolet, JAC México, KIA México, financiamiento autos nuevos, Nissan Versa 2026, Chevrolet Tracker, JAC E10X, KIA Rio, HEBEH Automotriz");
    set("name", "author", "HEBEH Automotriz");
    set("name", "robots", "index,follow,max-image-preview:large");
    set("property", "og:title", document.title);
    set("property", "og:description", desc);
    set("property", "og:type", "website");
    set("property", "og:site_name", "HEBEH Automotriz");
    set("property", "og:locale", "es_MX");
    if (typeof window !== "undefined") {
      set("property", "og:url", window.location.origin + "/");
    }
    // JSON-LD para el catálogo — AutoDealer con las 4 marcas.
    let ld = document.getElementById("hebeh-jsonld");
    if (!ld) {
      ld = document.createElement("script");
      ld.setAttribute("type", "application/ld+json");
      ld.id = "hebeh-jsonld";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AutoDealer",
      name: "HEBEH Automotriz",
      description: desc,
      url: typeof window !== "undefined" ? window.location.origin : "https://automotriz.hebeh.com.mx",
      areaServed: { "@type": "City", name: "Ciudad de México" },
      address: {
        "@type": "PostalAddress",
        addressCountry: "MX",
        addressRegion: "Ciudad de México",
      },
      brand: [
        { "@type": "Brand", name: "Nissan" },
        { "@type": "Brand", name: "Chevrolet" },
        { "@type": "Brand", name: "JAC" },
        { "@type": "Brand", name: "KIA" },
      ],
      priceRange: "$$",
      sameAs: ["https://hebeh.com.mx"],
    });
  }, []);

  const fetchAll = useCallback(async () => {
    // In-memory cache — avoid heavy refetch when navigating back from detail
    const cached = getCached("automotriz-catalog");
    if (cached) {
      setVehicles(cached.vehicles);
      setBrands(cached.brands);
      setRentalVehicles(cached.rentals);
      setInterestRate(cached.interestRate);
      if (cached.business) setBusiness((prev) => ({ ...prev, ...cached.business }));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [vRes, rRes, cRes] = await Promise.all([
        axios.get(`${API}/api/public/vehicles?business_id=automotriz`),
        axios.get(`${API}/api/public/rental-vehicles?business_id=automotriz`).catch(() => ({ data: { success: false } })),
        axios.get(`${API}/api/public/config?business_id=automotriz`).catch(() => ({ data: { success: false } })),
      ]);
      const data = {
        vehicles: vRes.data?.success ? (vRes.data.vehicles || []) : [],
        brands: vRes.data?.success ? (vRes.data.available_brands || []) : [],
        rentals: rRes.data?.success ? (rRes.data.vehicles || []) : [],
        interestRate: cRes.data?.success ? (cRes.data.interest_rate || 20) : 20,
        business: vRes.data?.business || null,
      };
      setVehicles(data.vehicles);
      setBrands(data.brands);
      setRentalVehicles(data.rentals);
      setInterestRate(data.interestRate);
      if (data.business) setBusiness((prev) => ({ ...prev, ...data.business }));
      setCached("automotriz-catalog", data);
    } catch (e) {
      console.error("Error loading catalog:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const source = viewMode === "rental" ? rentalVehicles : vehicles;
    let list = [...source];
    if (selectedCategory) {
      list = list.filter((v) => (v.category || "").toLowerCase() === selectedCategory.toLowerCase());
    }
    if (selectedBrand) list = list.filter((v) => v.brand?.toLowerCase() === selectedBrand.toLowerCase());
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      list = list.filter(
        (v) => v.model_name?.toLowerCase().includes(t) ||
               v.brand?.toLowerCase().includes(t) ||
               v.version?.toLowerCase().includes(t)
      );
    }
    if (viewMode === "sale") list = groupByModel(list);
    return list;
  }, [vehicles, rentalVehicles, viewMode, searchTerm, selectedBrand, selectedCategory]);

  const scrollToCatalog = useCallback(() => {
    // Retry-safe: el destino `#catalogo` vive dentro de VehicleGridPremium,
    // que solo se monta cuando termina de cargar. Reintentamos hasta 15 veces
    // (~1.5s) para no perder clics tempranos del usuario.
    const attempt = (retries = 15) => {
      const el = document.getElementById("catalogo");
      if (el) {
        const headerOffset = 72;
        const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top, behavior: "smooth" });
      } else if (retries > 0) {
        setTimeout(() => attempt(retries - 1), 100);
      }
    };
    attempt();
  }, []);
  const clear = () => { setSearchTerm(""); setSelectedBrand(""); setSelectedCategory(""); };
  const handleFilterCategory = (category) => {
    // Toggle: if user clicks the same category again, clear it (shows all)
    setSelectedCategory((prev) => (prev === category ? "" : category));
    setSearchTerm("");
    setSelectedBrand("");
    setTimeout(() => scrollToCatalog(), 60);
  };

  return (
    <div className="min-h-screen bg-[#0B1623] text-white font-manrope">
      <HeaderBar business={business} />

      <HeroAston onScrollToCatalog={scrollToCatalog} />

      <RimacTransition />

      <TeslaLineup onFilterCategory={handleFilterCategory} selectedCategory={selectedCategory} />

      <SearchPanel
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        brands={brands}
        viewMode={viewMode}
        setViewMode={setViewMode}
        salesCount={vehicles.length}
        rentalsCount={rentalVehicles.length}
        onClear={clear}
        hideRentalToggle={true}
      />

      {loading ? (
        <div className="bg-[#FFFFFF] flex items-center justify-center py-32">
          <Loader2 className="w-10 h-10 animate-spin text-[#F09A21]" />
        </div>
      ) : (
        <VehicleGridPremium
          vehicles={filtered}
          businessSlug={business.slug}
          isRental={viewMode === "rental"}
          accentColor={business.accentColor}
          title={viewMode === "rental" ? "Equipos en renta" : "Nuestro catálogo"}
          subtitle={viewMode === "rental"
            ? "Enviamos a toda la República. Cotización inmediata por WhatsApp."
            : "Cada modelo agrupa todas sus versiones. Ábrelo para elegir la que se ajusta a ti."}
        />
      )}

      <FinancingSimulator interestRate={interestRate} whatsapp={business.whatsapp} />
      <BenefitsSection />
      <HowItWorks />
      <StatsSection />
      <PremiumFooter business={business} />
      <FloatingActions business={business} />
      <AssistantWidget />
    </div>
  );
}
